/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import PropTypes from 'prop-types';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';

// Transient, self-dismissing result of a sync attempt — separate from
// OfflineBanner (which reflects ongoing connectivity/pending-count state)
// since this communicates a one-off outcome instead of a standing status.
// Bottom-center (variant="fixed", the default) so it never competes with
// OfflineBanner's top-right spot. variant="inline" drops the viewport-fixed
// positioning so a caller can place this same pill inline in its own layout
// instead — e.g. directly above a sticky action button, where a fixed
// bottom-of-viewport toast would otherwise land on top of/behind the button.
const SyncStatusToast = ({ status, message, onDismiss, variant = 'fixed' }) => {
  if (!status) return null;

  const isError = status === 'error';
  const isInline = variant === 'inline';

  return (
    <div
      role="status"
      className={`flex items-center gap-2 pl-3 pr-2 py-2 rounded-full shadow-lg text-[12px] font-semibold ${
        isError ? 'bg-error text-error-content' : 'bg-success text-success-content'
      } ${
        isInline ? 'w-full justify-center' : 'fixed left-1/2 -translate-x-1/2 z-[9998] max-w-[92vw]'
      }`}
      style={isInline ? undefined : { bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      {isError ? <AlertTriangle size={14} className="shrink-0" /> : <CheckCircle2 size={14} className="shrink-0" />}
      <span className="truncate">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
          isError ? 'hover:bg-error-content/20' : 'hover:bg-success-content/20'
        }`}
      >
        <X size={12} />
      </button>
    </div>
  );
};

SyncStatusToast.propTypes = {
  status: PropTypes.oneOf(['success', 'error']),
  message: PropTypes.string,
  onDismiss: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['fixed', 'inline']),
};

export default SyncStatusToast;
